import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiEyeLine,
} from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { PrettyBookingPaymentStatus } from '../../../../../../../models/bookings';
import { IBookingCancellationHistoryListItem } from '../../../../../../../services/Bookings/ListBookingCancellationHistoriesService';

export interface IBookingCancellationHistoryTableItem
  extends IBookingCancellationHistoryListItem {
  formattedBookedDate: string;
  formattedCreatedAt: string;
  formattedEndTime: string;
  formattedStartTime: string;
}

interface IBookingCancellationHistoryTableProps extends BoxProps {
  cancellationHistories: IBookingCancellationHistoryTableItem[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'minutesBeforeScheduleStart' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  sortBy?: string;
  totalPages?: number;
}

export const BookingCancellationHistoryTable = ({
  cancellationHistories,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  ...rest
}: IBookingCancellationHistoryTableProps): JSX.Element => {
  const filterByOptions = [
    {
      label: 'Nome',
      value: 'userName',
    },
    {
      label: 'Documento',
      value: 'userDocument',
    },
  ];

  const sortByOptions = [
    {
      label: 'Cancelado em',
      value: 'createdAt',
    },
    {
      label: 'Minutos antes do início do evento',
      value: 'minutesBeforeScheduleStart',
    },
  ];

  return (
    <Box {...rest}>
      <TableFilters
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        filterInputType={filterBy === 'bookedDate' ? 'datepicker' : 'input'}
        onChangeFilterBy={onChangeFilterBy}
        onFilter={onFilter}
        onOrder={onOrder}
        onSort={(sort) =>
          !!onSort && onSort(sort as 'minutesBeforeScheduleStart' | 'createdAt')
        }
        order={order}
        sortBy={sortBy}
        sortByOptions={sortByOptions}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Reservado para</Th>
            <Th>Cancelado por</Th>
            <Th>Motivo</Th>
            <Th textAlign="center">Minutos antes do início do evento</Th>
            <Th textAlign="center">Cancelado após limite</Th>
            <Th textAlign="center">Horário reservado</Th>
            <Th textAlign="center">Horário do cancelamento</Th>
            <Th>Status pagamento</Th>
            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody fontSize="sm">
          {cancellationHistories.map((cancellationHistory) => (
            <Tr key={cancellationHistory.id}>
              <Td>{cancellationHistory.booking.user.name}</Td>

              <Td>{cancellationHistory.user.name}</Td>

              <Td>{cancellationHistory.reason ?? '-'}</Td>

              <Td textAlign="center">
                {cancellationHistory.minutesBeforeScheduleStart}
              </Td>

              <Td textAlign="center">
                {cancellationHistory.exceedCancellationDeadline ? (
                  <Icon as={RiCheckboxCircleLine} color="green" />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} color="gray.400" />
                )}
              </Td>

              <Td textAlign="center">
                <Box>
                  <Text>{cancellationHistory.formattedBookedDate}</Text>
                  <Text>{`${cancellationHistory.formattedStartTime} - ${cancellationHistory.formattedEndTime}`}</Text>
                </Box>
              </Td>

              <Td textAlign="center">
                <Text>
                  {cancellationHistory.formattedCreatedAt.split('-')[0]}
                </Text>
                <Text>
                  {cancellationHistory.formattedCreatedAt.split('-')[1]}
                </Text>
              </Td>

              <Td>
                {cancellationHistory.booking.payment
                  ? PrettyBookingPaymentStatus[
                      cancellationHistory.booking.payment.status
                    ]
                  : 'Isento'}
              </Td>

              <Td textAlign="center">
                <LinkButton
                  to={{
                    pathname: '/bookings/details',
                    state: {
                      bookingId: cancellationHistory.bookingId,
                    },
                  }}
                >
                  <Icon as={RiEyeLine} fontSize="16" />
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
